(() => {
  const labels = { all: 'All stories', history: 'The History of', reviews: 'Reviews', guides: 'Guides', notes: 'Store notes' };
  const normalise = value => value.normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  function init(root) {
    if (root.dataset.journalReady) return;
    root.dataset.journalReady = 'true';
    const get = selector => root.querySelector(selector);
    const grid = get('[data-journal-grid]');
    const input = get('[data-journal-query]');
    const form = get('[data-journal-search]');
    const sort = get('[data-journal-sort]');
    const feature = get('[data-journal-feature]');
    const series = get('[data-journal-series]');
    const status = get('[data-journal-status]');
    const more = get('[data-journal-more]');
    const pagination = get('[data-journal-pagination]');
    const error = get('[data-journal-error]');
    const totalPages = Number(root.dataset.pages) || 1;
    const currentPage = Number(root.dataset.page) || 1;
    let cards = [...grid.querySelectorAll('[data-journal-card]')];
    let complete = totalPages === 1;
    let loading = null;
    let failed = false;
    let limit = 6;
    let topic = 'all';
    let query = '';
    let order = 'newest';
    const searchText = new WeakMap();
    const textFor = card => {
      if (!searchText.has(card)) searchText.set(card, normalise(card.dataset.search || ''));
      return searchText.get(card);
    };
    const filtered = () => topic !== 'all' || query.trim() !== '' || order !== 'newest';
    function readState() {
      const params = new URL(location.href).searchParams;
      topic = Object.hasOwn(labels, params.get('topic')) ? params.get('topic') : 'all';
      query = params.get('q') || '';
      order = params.get('sort') === 'oldest' ? 'oldest' : 'newest';
      input.value = query;
      sort.value = order;
      limit = 6;
    }
    function writeState(push = false) {
      const url = new URL(location.href);
      topic === 'all' ? url.searchParams.delete('topic') : url.searchParams.set('topic', topic);
      query.trim() ? url.searchParams.set('q', query.trim()) : url.searchParams.delete('q');
      order === 'newest' ? url.searchParams.delete('sort') : url.searchParams.set('sort', order);
      if (complete) url.searchParams.delete('page');
      if (url.href !== location.href) history[push ? 'pushState' : 'replaceState']({}, '', url);
    }
    function render() {
      const isFiltered = filtered();
      const terms = normalise(query).split(' ').filter(Boolean);
      root.classList.toggle('is-filtered', isFiltered);
      if (feature) feature.hidden = isFiltered;
      if (series) series.hidden = isFiltered;
      get('[data-journal-title]').textContent = query.trim() ? 'Search results' : topic !== 'all' ? labels[topic] : feature && !isFiltered ? 'More from the journal' : 'From the archive';
      get('[data-journal-clear]').hidden = !isFiltered;
      root.querySelectorAll('[data-topic-button]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.topicButton === topic)));
      root.querySelectorAll('[data-topic-count]').forEach(span => {
        span.textContent = complete ? String(cards.filter(card => span.dataset.topicCount === 'all' || card.dataset.topic === span.dataset.topicCount).length) : '';
      });
      cards.sort((a, b) => (Number(b.dataset.date) - Number(a.dataset.date)) * (order === 'oldest' ? -1 : 1));
      const matches = cards.filter(card => (topic === 'all' || card.dataset.topic === topic) && terms.every(term => textFor(card).includes(term) || textFor(card).replace(/\s/g, '').includes(term)) && !(feature && !isFiltered && card.hasAttribute('data-lead')));
      const shown = new Set(matches.slice(0, limit));
      cards.forEach(card => { card.hidden = !shown.has(card); grid.append(card); });
      const n = shown.size;
      const noun = matches.length === 1 ? 'story' : 'stories';
      status.textContent = loading ? 'Searching the full journal…' : `${n === matches.length ? matches.length : `${n} of ${matches.length}`} ${noun}${complete ? '' : ' on this page'}${query.trim() ? ` matching “${query.trim()}”` : ''}`;
      get('[data-journal-empty]').hidden = matches.length > 0 || Boolean(loading);
      more.hidden = matches.length <= limit;
      more.textContent = `Show ${Math.min(6, matches.length - limit)} more stories ↓`;
      pagination.hidden = complete;
      error.hidden = !failed;
    }
    async function loadAll() {
      if (complete) return;
      if (loading) return loading;
      failed = false;
      loading = (async () => {
        const seen = new Set(cards.map(card => card.dataset.id));
        for (let page = 1; page <= totalPages; page++) {
          if (page === currentPage) continue;
          const url = new URL(root.dataset.baseUrl, location.origin);
          url.searchParams.set('section_id', root.dataset.section);
          url.searchParams.set('page', page);
          const response = await fetch(url, { credentials: 'same-origin' });
          if (!response.ok) throw new Error('Archive request failed');
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const nextCards = [...doc.querySelectorAll('[data-journal-card]')];
          if (!nextCards.length) throw new Error('Archive page missing');
          nextCards.forEach(card => {
            if (!seen.has(card.dataset.id)) { seen.add(card.dataset.id); cards.push(document.importNode(card, true)); }
          });
        }
        complete = true;
      })().catch(() => { failed = true; }).finally(() => { loading = null; render(); });
      render();
      return loading;
    }
    function update({ push = false, fetchArchive = true } = {}) {
      limit = 6;
      writeState(push);
      render();
      if (fetchArchive && filtered()) loadAll();
    }
    function reset() {
      topic = 'all'; query = ''; order = 'newest'; input.value = ''; sort.value = 'newest';
      update({ push: true, fetchArchive: false });
    }
    root.querySelectorAll('[data-topic-button]').forEach(button => button.addEventListener('click', () => { topic = button.dataset.topicButton; update({ push: true }); if (!complete) loadAll(); }));
    input.addEventListener('input', () => { query = input.value; update(); });
    form.addEventListener('submit', event => { event.preventDefault(); query = input.value; update(); });
    sort.addEventListener('change', () => { order = sort.value; update({ push: true }); if (!complete) loadAll(); });
    get('[data-journal-clear]').addEventListener('click', reset);
    get('[data-journal-reset]').addEventListener('click', () => { reset(); input.focus(); });
    get('[data-journal-retry]').addEventListener('click', () => loadAll());
    more.addEventListener('click', () => {
      const previouslyVisible = new Set(cards.filter(card => !card.hidden));
      limit += 6; render();
      cards.find(card => !card.hidden && !previouslyVisible.has(card))?.querySelector('a')?.focus();
    });
    window.addEventListener('popstate', () => { if (root.isConnected) { readState(); render(); if (filtered()) loadAll(); } });
    get('[data-journal-topics]').hidden = false;
    get('[data-journal-sort-wrap]').hidden = false;
    readState(); render(); if (filtered()) loadAll();
  }
  const start = scope => scope.querySelectorAll('[data-journal]').forEach(init);
  start(document);
  document.addEventListener('shopify:section:load', event => start(event.target));
})();
